import React from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { PLATFORM_META, PLATFORM_ORDER } from "@/lib/platforms";

export function GetStarted({ user, data }) {
  const navigate = useNavigate();
  const hasEmail = !!user?.email;
  const hasQueried = (data?.total_queries || 0) > 0;
  const isPaid = data?.membership_tier && data.membership_tier !== "free";
  const steps = [
    {
      done: hasEmail,
      title: "Profil e-postanı ekle",
      hint: "Kurtarma ve bildirimler için gerekli",
      to: "/profil",
    },
    {
      done: hasQueried,
      title: "İlk sorgunu çalıştır",
      hint: "Discord ID Sorgu ile başla",
      to: "/checker/discord",
    },
    {
      done: !!isPaid,
      title: "Planını yükselt veya kredi ekle",
      hint: "Günlük limitini artır (BTC ile)",
      to: "/magaza",
    },
  ];
  const remaining = steps.filter((s) => !s.done).length;
  return (
    <div className="gc-card p-5" data-testid="get-started">
      <div className="flex items-center justify-between mb-1">
        <div className="gc-label">BAŞLARKEN</div>
        <span className="text-[10px] font-mono text-[#A1A1AA]">
          {steps.length - remaining} / {steps.length} tamamlandı
        </span>
      </div>
      <div className="font-heading text-lg font-bold mb-4">
        {remaining > 0 ? `${remaining} adım kaldı` : "Hepsi tamam ✓"}
      </div>
      <div className="space-y-2">
        {steps.map((s, i) => (
          <button
            key={i}
            onClick={() => navigate(s.to)}
            data-testid={`gs-step-${i}`}
            className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
              s.done
                ? "border-[#00FF66]/25 bg-[#00FF66]/5"
                : "border-[#1a1a1a] bg-[#080808] hover:border-[#9D4EDD]/50"
            }`}
          >
            {s.done ? (
              <CheckCircle2 size={16} className="text-[#00FF66] shrink-0" />
            ) : (
              <Circle size={16} className="text-[#A1A1AA] shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div
                className={`text-sm font-semibold ${
                  s.done ? "text-[#A1A1AA] line-through" : "text-white"
                }`}
              >
                {s.title}
              </div>
              <div className="text-[11px] text-[#666] font-mono mt-0.5">{s.hint}</div>
            </div>
            <ArrowRight size={13} className="text-[#525252] shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

export function BrowseByCategory() {
  const navigate = useNavigate();
  return (
    <section className="gc-card p-5" data-testid="browse-category">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="gc-label">KATEGORİLERE GÖZ AT</div>
          <div className="font-heading text-lg font-bold mt-1">Sorgu Modülleri</div>
        </div>
        <span className="text-[10px] font-mono text-[#525252]">
          Bir modüle tıkla, hemen sorgula
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2.5">
        {PLATFORM_ORDER.map((p) => {
          const meta = PLATFORM_META[p];
          const Icon = meta.Icon;
          return (
            <button
              key={p}
              onClick={() => navigate(`/checker/${p}`)}
              data-testid={`cat-chip-${p}`}
              className="flex items-center gap-2 border border-[#1a1a1a] bg-[#080808] hover:border-[#9D4EDD]/50 hover:bg-[#0f0f0f] p-2.5 rounded-xl transition-colors group"
              title={meta.name}
            >
              <span
                className="w-7 h-7 flex items-center justify-center border border-[#141414] bg-[#050505] rounded-lg shrink-0"
                style={{ color: meta.color }}
              >
                <Icon size={13} />
              </span>
              <span className="text-xs text-[#EDEDED] group-hover:text-white truncate">
                {meta.short}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function QuickLinks() {
  const navigate = useNavigate();
  const links = [
    ["Discord Sorgu", "/checker/discord"],
    ["Multi-Platform Modülleri", "/checker/instagram"],
    ["Üyelik & Kredi", "/magaza"],
    ["Hesap Ayarları", "/profil"],
    ["Duyurular", "/duyurular"],
  ];
  return (
    <div className="gc-card p-5" data-testid="quick-links">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1 h-3.5 bg-[#9D4EDD] rounded-full" />
        <div className="font-heading text-base font-bold">Kısayollar</div>
      </div>
      <div className="space-y-1">
        {links.map(([label, to]) => (
          <button
            key={to}
            onClick={() => navigate(to)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-transparent hover:border-[#1a1a1a] hover:bg-[#0a0a0a] text-left transition-colors"
          >
            <span className="text-sm text-[#EDEDED]">{label}</span>
            <ArrowRight size={13} className="text-[#525252]" />
          </button>
        ))}
      </div>
    </div>
  );
}

export function Footer() {
  return (
    <footer
      className="gc-card p-4 md:p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3"
      data-testid="dashboard-footer"
    >
      <div className="flex items-center gap-2 text-xs font-mono text-[#525252]">
        <span className="w-1.5 h-1.5 bg-[#00FF66] rounded-full animate-pulse" />
        SESSION_ENCRYPTED · JWT_HS256 · BCRYPT
      </div>
      <div className="flex items-center gap-4 text-xs">
        <span className="text-[#525252]">Topluluğa katıl:</span>
        <a
          href="#"
          className="text-[#9D4EDD] hover:text-white transition-colors"
          onClick={(e) => e.preventDefault()}
        >
          Telegram
        </a>
        <a
          href="#"
          className="text-[#9D4EDD] hover:text-white transition-colors"
          onClick={(e) => e.preventDefault()}
        >
          Canlı destek
        </a>
        <a
          href="#"
          className="text-[#9D4EDD] hover:text-white transition-colors"
          onClick={(e) => e.preventDefault()}
        >
          Sistem durumu
        </a>
      </div>
    </footer>
  );
}
